// ADR 23.9.26u §8 / 25.9.26g §1 / 25.9.26h §1–§2 — which install path, if any,
// the account menu and the install strip offer. Pure over its inputs, so the
// whole table is a unit test.
//
// Platform detection is a predicate over the UA string, no
// `navigator.userAgentData` (Chromium-only, and asynchronous on some builds). `ios` means any browser on
// iOS, not "iOS Safari": since 16.4 Chrome, Firefox and Edge there can Add to
// Home Screen from their share menu too (this refines roadmap D1's iOS row).
//
// THE PLATFORM MATRIX (25.9.26h Context). Steps are offered where a browser's
// own menu performs the install: Chrome on Windows, macOS, Linux and ChromeOS;
// Edge on Windows and macOS; Chrome and Firefox on Android; Firefox 143+ on
// Windows. Firefox on macOS and Linux cannot install, Edge on Linux is not
// captured by §0, and the Chromium derivatives' menus differ — all `other`.
// Since 15.10.26c §1 the browser the phone came with has its own row on
// Android: Samsung Internet, Xiaomi's Mi Browser, Huawei Browser (EMUI and
// HarmonyOS 2–4), OPPO/realme's HeyTap and vivo's browser, each floored by its
// own token's major. HarmonyOS NEXT carries no `Android` and stays `other`.
//
// `isInAppBrowser`'s `MicroMessenger` arm overlaps 23.9.26h's planned
// `wechatContext(ua)`. That ADR is not on disk yet, so by the reader rule it
// adopts this arm when it lands, rather than the other way round.
//
// THE iOS CEILING (6.10.26a §3d). iOS has no programmatic install: WebKit
// implements neither `beforeinstallprompt` nor the Web Install API, and every
// iOS browser is WebKit. Share → Add to Home Screen → Add is the shortest
// path; the most this file can do is say where Share is (`iosBrowserOf`).
//
// THE HANDOFF (6.10.26b §2). An `other` browser that cannot install is not
// always a dead end: when `handoff.ts` names a browser the OS guarantees
// (Edge on Windows, Chrome on Android) or one worth a copied link (macOS,
// Linux), the answer is `handoff`, ranked below every install this browser
// can do itself. `other` with no target is still `none`.

// Type-only: erased at build, so `handoff.ts` importing this file's regexes is no cycle.
import type { HandoffTarget } from './handoff'

export type Platform =
  | 'ios' | 'mac_safari'
  | 'chrome_desktop' | 'edge_desktop'
  | 'chrome_android' | 'firefox_android' | 'firefox_windows'
  | OemPlatform
  | 'other'
export type Affordance =
  | 'none' | 'prompt' | 'ios_instructions' | 'mac_instructions' | 'menu_instructions' | 'handoff'

type OemPlatform = 'samsung_android' | 'xiaomi_android' | 'huawei_android' | 'oppo_android' | 'vivo_android'
type FlooredPlatform = 'chrome_desktop' | 'edge_desktop' | 'chrome_android' | 'firefox_android' | OemPlatform

/**
 * ADR 25.9.26h §0 — the lowest major each menu-steps row trusts. PROVISIONAL
 * until the §0 capture: set from the browsers' release notes (cited per floor
 * in the ADR's As built), then confirmed or replaced by the capture before
 * deploy. One constant, so that update is one line the tests follow.
 */
export const MENU_STEPS_FLOORS: Record<FlooredPlatform, number> = {
  chrome_desktop: 130,
  edge_desktop: 120,
  chrome_android: 120,
  firefox_android: 136,
  // PROVISIONAL (15.10.26c §0) — each maker floor is the token major of the
  // ADR's public sample; Samsung's is replaced by the §0 probe before deploy,
  // the rest by 15.10.26d's field reports.
  samsung_android: 25,
  xiaomi_android: 14,
  huawei_android: 12,
  oppo_android: 45,
  vivo_android: 14,
}

/** 15.10.26c §1 — each maker's browser by its UA token. Shared with 15.10.26d's field reports. */
export const OEM_TOKENS: readonly (readonly [token: string, platform: OemPlatform])[] = [
  ['SamsungBrowser', 'samsung_android'],
  ['MiuiBrowser', 'xiaomi_android'],
  ['HuaweiBrowser', 'huawei_android'],
  ['HeyTapBrowser', 'oppo_android'],
  ['VivoBrowser', 'vivo_android'],
]

/** 25.9.26h §1 rule 6 — Firefox 143 shipped taskbar web apps on Windows. Fixed, not §0-gated. */
const FIREFOX_WINDOWS_MIN_MAJOR = 143

/** Tokens a non-Safari browser adds beside `Safari/` in its UA. Shared with `handoff.ts` (6.10.26b §1). */
export const NOT_SAFARI =/Chrome\/|Chromium\/|CriOS\/|Edg\/|Firefox\/|FxiOS\/|OPR\//

/**
 * 25.9.26h §1 rule 2 — Chromium derivatives (and Android WebView) that carry
 * `Chrome/` but whose menu differs or cannot install. Shared with `handoff.ts`
 * (6.10.26b §1), whose "plain Chrome" excludes the same list. The five maker
 * tokens stay here (15.10.26c §1) even though `oemAndroidPlatform` reads them
 * first: this is what keeps them out of `isPlainChrome`, so a maker's browser
 * below its floor is still handed to Chrome.
 */
export const EXCLUDED_DERIVATIVES =
  /OPR\/|SamsungBrowser\/|YaBrowser\/|Vivaldi\/|EdgA\/|DuckDuckGo\/|UCBrowser\/|MiuiBrowser\/|HuaweiBrowser\/|HeyTapBrowser\/|VivoBrowser\/|; wv\)/

const CHROME_DESKTOP_OS = /Windows NT|Macintosh|X11; Linux|CrOS/

/**
 * ADR 25.9.26g §1 — Safari 26 is the first that ships only for macOS Sonoma
 * and later, so the first whose UA guarantees *File → Add to Dock* exists.
 * Safari reports every macOS as `10_15_7`, so the OS itself is unreadable.
 */
const MAC_SAFARI_MIN_MAJOR = 26

function isMacSafari(userAgent: string): boolean {
  if (!/Macintosh/.test(userAgent)) return false
  if (!userAgent.includes('Safari/') || NOT_SAFARI.test(userAgent)) return false
  const version = /Version\/(\d+)/.exec(userAgent)
  return version !== null && Number(version[1]) >= MAC_SAFARI_MIN_MAJOR
}

/** The major version after `<token>/`, or 0 when absent (which no floor admits). */
export function majorOf(userAgent: string, token: string): number {
  const match = new RegExp(`${token}/(\\d+)`).exec(userAgent)
  return match === null ? 0 : Number(match[1])
}

/** A matched browser below its floor ends evaluation as `other` — it never falls through. */
function floored(platform: FlooredPlatform, major: number): Platform {
  return major >= MENU_STEPS_FLOORS[platform] ? platform : 'other'
}

/** 15.10.26c §1 — the browser the phone came with. Android only, never a WebView; null for anything else. */
function oemAndroidPlatform(userAgent: string): Platform | null {
  if (!userAgent.includes('Android') || userAgent.includes('; wv)')) return null
  for (const [token, platform] of OEM_TOKENS) {
    if (userAgent.includes(`${token}/`)) return floored(platform, majorOf(userAgent, token))
  }
  return null
}

/** §1 rules 4–5. */
function androidPlatform(userAgent: string): Platform {
  if (userAgent.includes('Chrome/')) return floored('chrome_android', majorOf(userAgent, 'Chrome'))
  if (userAgent.includes('Firefox/')) return floored('firefox_android', majorOf(userAgent, 'Firefox'))
  return 'other'
}

/** §1 rules 3, 6 and 7 — the order matters: an Edge UA also carries `Chrome/`. */
function desktopPlatform(userAgent: string): Platform {
  if (userAgent.includes('Edg/')) {
    const edgeOs = /Windows NT|Macintosh/.test(userAgent)
    return edgeOs ? floored('edge_desktop', majorOf(userAgent, 'Edg')) : 'other'
  }
  if (userAgent.includes('Firefox/')) {
    const windows = userAgent.includes('Windows NT')
    return windows && majorOf(userAgent, 'Firefox') >= FIREFOX_WINDOWS_MIN_MAJOR ? 'firefox_windows' : 'other'
  }
  if (userAgent.includes('Chrome/') && CHROME_DESKTOP_OS.test(userAgent)) {
    return floored('chrome_desktop', majorOf(userAgent, 'Chrome'))
  }
  return 'other'
}

/**
 * iPhone / iPad / iPod, or iPadOS's desktop UA (`Macintosh` with a touch
 * screen) — both checked first, so an iPad is never read as a Mac. Then Mac
 * Safari 26+; then 25.9.26h §1's browser rows, first match wins.
 */
export function platformOf(userAgent: string, maxTouchPoints: number): Platform {
  if (/iPhone|iPad|iPod/.test(userAgent)) return 'ios'
  if (/Macintosh/.test(userAgent) && maxTouchPoints > 1) return 'ios'
  if (isMacSafari(userAgent)) return 'mac_safari'
  const oem = oemAndroidPlatform(userAgent)
  if (oem !== null) return oem
  if (EXCLUDED_DERIVATIVES.test(userAgent)) return 'other'
  return userAgent.includes('Android') ? androidPlatform(userAgent) : desktopPlatform(userAgent)
}

/** WeChat, Facebook and Instagram's in-app browsers cannot add to the home screen. */
export function isInAppBrowser(userAgent: string): boolean {
  return /MicroMessenger|FBAN|FBAV|Instagram/.test(userAgent)
}

export interface AffordanceInput {
  platform: Platform
  standalone: boolean
  promptAvailable: boolean
  inAppBrowser: boolean
  /** 25.9.26h §4 — `getInstalledRelatedApps` found this app installed. */
  installedElsewhere: boolean
  /** 6.10.26b §2 — `handoffTargetOf`'s answer, passed only for an `other` platform. */
  handoffTarget: HandoffTarget | null
}

/**
 * Already installed (here or elsewhere), or in an in-app browser: nothing. A
 * captured `beforeinstallprompt`: our prompt. iOS without one: the Share-sheet
 * steps. Mac Safari 26+: the Dock steps. One of 25.9.26h's five browser
 * platforms, or 15.10.26c's five maker browsers: its menu steps. Anything else (Firefox on macOS and Linux, the
 * derivatives, a browser below its floor): the handoff when there is a
 * target (6.10.26b), else nothing.
 */
export function installAffordance(input: AffordanceInput): Affordance {
  if (input.standalone || input.inAppBrowser || input.installedElsewhere) return 'none'
  if (input.promptAvailable) return 'prompt'
  if (input.platform === 'ios') return 'ios_instructions'
  if (input.platform === 'mac_safari') return 'mac_instructions'
  if (input.platform !== 'other') return 'menu_instructions'
  return input.handoffTarget === null ? 'none' : 'handoff'
}

/** 6.10.26a §2 — the device class a platform's copy speaks to. */
export type Device = 'computer' | 'handheld' | 'unknown'

const DEVICES: Record<Platform, Device> = {
  chrome_desktop: 'computer',
  edge_desktop: 'computer',
  mac_safari: 'computer',
  firefox_windows: 'computer',
  ios: 'handheld',
  chrome_android: 'handheld',
  firefox_android: 'handheld',
  samsung_android: 'handheld',
  xiaomi_android: 'handheld',
  huawei_android: 'handheld',
  oppo_android: 'handheld',
  vivo_android: 'handheld',
  other: 'unknown',
}

/**
 * `other` is a derivative that fired the prompt (Opera, Vivaldi…),
 * which could be either. "Handheld", not "phone": `ios` includes the iPad.
 */
export function deviceOf(platform: Platform): Device {
  return DEVICES[platform]
}

/** 6.10.26a §3b — which iOS browser, so the steps can say where its Share is. */
export type IosBrowser = 'safari' | 'chrome' | 'edge' | 'firefox' | 'other'

/**
 * Meaningful only for an `ios` platform; `platformOf` is unchanged. Chrome and
 * Edge on iPad request the desktop site by default and send a Safari-shaped
 * UA, so they read as `safari` and get the `ipad_safari` install guide
 * (16.10.26g): a known mismatch until the device walk confirms where their
 * Share is (roadmap 16.10.26c D8, D10). No `Safari/` + `Version/` is an in-app or unknown WebKit shell: `other`.
 */
export function iosBrowserOf(userAgent: string): IosBrowser {
  if (userAgent.includes('CriOS/')) return 'chrome'
  if (userAgent.includes('EdgiOS/')) return 'edge'
  if (userAgent.includes('FxiOS/')) return 'firefox'
  return userAgent.includes('Safari/') && userAgent.includes('Version/') ? 'safari' : 'other'
}
